import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import CustomCheckbox from '../root/components/common/checkbox'
import { fetchTasks, createTask, updateTask } from './api/locations-api'
import { useSelector } from 'react-redux'
import { Button } from '../root/components/common'
import { makeStyles } from '@material-ui/core/styles'
import { Input } from '../root/components/common'
import Loading from '../root/components/common/loading'
import { useDispatch } from 'react-redux'
import { enqueueSnackbar } from '../root/actions'

const useStyles = makeStyles(theme => ({
    wrapper: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    titleText: {
        color: theme.quaternary
    },
    taskList: {
        listStyleType: 'none',
        padding: 0,
        margin: 0
    },
    separator: {
        marginTop: '20px',
        borderColor: '#eee'
    },
    inputWrapper: {
        display: 'flex',
        flexDirection: 'column'
    },
    buttonGroup: {
        marginTop: '8px',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'center'
    },
    saveButton: {
        marginRight: '8px'
    }
}))

const TasksComponent = () => {
    const [newTask, setNewTask] = useState('')
    const isValidTaskLength = value => value.length <= 1024
    const [showCompleted, setShowCompleted] = useState(false)
    const [showInputField, setShowInputField] = useState(false)
    const [isCancelHovered, setIsCancelHovered] = useState(false)
    const { location } = useSelector(state => state.locations) || {}
    const { locale } = useSelector(state => state.main)
    const queryClient = useQueryClient()
    const classes = useStyles()
    const dispatch = useDispatch()

    const { isLoading, data: tasks = [] } = useQuery(
        ['location-tasks', location?.id],
        () => fetchTasks(location.id),
        {
            onError: e => {
                dispatch(
                    enqueueSnackbar({
                        message: locale.locations.tasksFetchFail,
                        options: {
                            variant: 'error'
                        }
                    })
                )
            }
        }
    )

    const { mutate: createNewTask } = useMutation(data => createTask(data), {
        onSuccess: () => {
            queryClient.invalidateQueries('location-tasks')
            dispatch(
                enqueueSnackbar({
                    message: locale.locations.taskCreated,
                    options: {
                        variant: 'success'
                    }
                })
            )
        },
        onError: () => {
            dispatch(
                enqueueSnackbar({
                    message: locale.locations.taskCreateFail,
                    options: {
                        variant: 'error'
                    }
                })
            )
        }
    })

    const { mutate: updateExistingTask } = useMutation(
        data => updateTask(data.taskId, data.is_completed),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('location-tasks')
                dispatch(
                    enqueueSnackbar({
                        message: locale.locations.taskUpdated,
                        options: {
                            variant: 'success'
                        }
                    })
                )
            },
            onError: () => {
                dispatch(
                    enqueueSnackbar({
                        message: locale.locations.taskUpdateFail,
                        options: {
                            variant: 'error'
                        }
                    })
                )
            }
        }
    )

    const handleAddTask = () => {
        if (newTask.trim()) {
            if (isValidTaskLength(newTask.trim())) {
                createNewTask({
                    locationId: location?.id,
                    description: newTask.trim()
                })
                setNewTask('')
                setShowInputField(false)
            } else {
                dispatch(
                    enqueueSnackbar({
                        message: locale.locations.taskDescriptionTooLong,
                        options: {
                            variant: 'error'
                        }
                    })
                )
            }
        }
    }

    const handleTaskClick = task => {
        updateExistingTask({
            taskId: task.id,
            is_completed: !task.is_completed
        })
    }

    const handleToggleCompleted = () => {
        setShowCompleted(!showCompleted)
    }

    const handleInputBlur = () => {
        if (!isCancelHovered) {
            handleAddTask()
        }
    }

    const handleKeyDown = e => {
        if (e.key === 'Enter') {
            handleAddTask()
        }
    }

    const handleShowInputField = () => {
        setShowInputField(true)
    }

    const handleCancelTask = () => {
        setNewTask('')
        setShowInputField(false)
        setIsCancelHovered(false)
    }
    const handleSaveTask = () => {
        handleAddTask()
    }

    const filteredTasks = showCompleted
        ? tasks
              .slice()
              .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
        : tasks
              .filter(task => !task.is_completed || task.isNew)
              .slice()
              .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))

    const hasCompletedTasks = tasks.some(task => task.is_completed)

    return (
        <div>
            <div className={classes.wrapper}>
                <h2 className={classes.titleText}>
                    {locale.locations.tasksAtThisLocation}
                </h2>
                <Button
                    disabled={!hasCompletedTasks}
                    size="small"
                    onClick={handleToggleCompleted}
                >
                    {showCompleted
                        ? locale.locations.hideCompletedTasks
                        : locale.locations.showCompletedTasks}
                </Button>
            </div>
            {isLoading ? (
                <Loading />
            ) : (
                <ul className={classes.taskList}>
                    {filteredTasks.map(task => (
                        <li key={task.id}>
                            <CustomCheckbox
                                className={classes.checkbox}
                                label={task.description}
                                checked={task.is_completed}
                                onClick={() => handleTaskClick(task)}
                            />
                        </li>
                    ))}
                </ul>
            )}
            <hr className={classes.separator} />
            {showInputField ? (
                <div className={classes.inputWrapper}>
                    <Input
                        multiline
                        minRows={1}
                        maxRows={4}
                        type="text"
                        autoFocus
                        value={newTask}
                        setValue={setNewTask}
                        onBlur={handleInputBlur}
                        onKeyDown={handleKeyDown}
                        placeholder={locale.locations.createNewTask}
                        style={{ width: '50%' }}
                        isError={!isValidTaskLength(newTask.trim())}
                        errorText={locale.locations.taskDescriptionTooLong}
                    />
                    <div className={classes.buttonGroup}>
                        <Button
                            disabled={
                                !newTask.trim() ||
                                !isValidTaskLength(newTask.trim())
                            }
                            className={classes.saveButton}
                            onClick={handleSaveTask}
                        >
                            {locale.common.save}
                        </Button>
                        <Button
                            reverted
                            onClick={handleCancelTask}
                            onMouseEnter={() => setIsCancelHovered(true)}
                            onMouseLeave={() => setIsCancelHovered(false)}
                        >
                            {locale.common.cancel}
                        </Button>
                    </div>
                </div>
            ) : (
                <Button
                    reverted
                    style={{
                        background: 'none',
                        border: 'none',
                        color: '#888',
                        cursor: 'pointer',
                        padding: 0,
                        fontSize: '16px',
                        textDecoration: 'none',
                        '&:hover': {}
                    }}
                    onClick={handleShowInputField}
                >
                    {locale.locations.createNewTask}
                </Button>
            )}
        </div>
    )
}

export default TasksComponent
